import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { type FC, forwardRef, type PropsWithChildren, type ReactNode } from "react";
import { Link, type LinkProps } from "react-router";

import { cn } from "../utils";
import { Alert, AlertDescription, AlertTitle } from "./alert";
import { Button, type ButtonProps } from "./button";
import { ButtonGroup } from "./button-group";
import { Group } from "./group";
import { Icon, type MaterialSymbol } from "./icon";
import { Skeleton } from "./skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip";

export interface GridProps extends React.HTMLAttributes<HTMLDivElement> {
  asChild?: boolean;
}

export const Grid = ({ asChild, className, ...props }: GridProps) => {
  const Comp = asChild ? Slot : "div";
  return <Comp className={cn("bg-inherit divide-y gap-x-4 grid auto-rows-min px-6 text-xs", className)} {...props} />;
};

export const gridRowClassName = [
  "col-span-full grid grid-cols-subgrid items-center -mx-6 px-6 relative",
  "bg-card min-h-12 last:shadow-border-b last:in-[.bg-card]:shadow-none in-[.bg-card]:bg-transparent",
  "data-[dragging=true]:bg-card! data-[dragging=true]:border! data-[dragging=true]:-mx-[calc(1.5rem+1px)]!",
];

const gridRowHeaderClassName = "bg-inherit font-semibold min-h-10 text-2xs text-muted-foreground";

export interface GridRowProps extends React.HTMLAttributes<HTMLDivElement> {
  asChild?: boolean;
}

export const GridRow = ({ asChild, className, ...props }: GridRowProps) => {
  const Comp = asChild ? Slot : "div";
  return <Comp className={cn(gridRowClassName, className)} {...props} />;
};

const gridRowHeaderVariants = cva([gridRowClassName[0], gridRowHeaderClassName], {
  variants: {
    position: {
      relative: "",
      sticky: "bg-background border-b -mb-px top-0 sticky z-10",
    },
  },
  defaultVariants: {
    position: "relative",
  },
});

export interface GridRowHeaderProps
  extends React.HTMLAttributes<HTMLElement>,
    VariantProps<typeof gridRowHeaderVariants> {}

export const GridRowHeader = ({ className, position, ...props }: GridRowHeaderProps) => (
  <header className={cn(gridRowHeaderVariants({ position }), className)} {...props} />
);

export const GridRowFooter = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <footer className={cn(gridRowClassName[0], gridRowHeaderClassName, className)} {...props} />
);

export const GridRowLink = forwardRef<HTMLAnchorElement, LinkProps>(({ children, className, ...props }, ref) => (
  <Link ref={ref} className={cn(gridRowClassName, "group", className)} {...props}>
    {children}
    <Icon
      icon="arrow_forward"
      className={cn(
        "flex items-center justify-center pointer-events-none rounded-full ",
        "opacity-25 group-hover:opacity-100 transition-all",
        "absolute top bottom right-4 group-hover:right-3 m-auto size-4"
      )}
    />
  </Link>
));
GridRowLink.displayName = "GridRowLink";

export interface GridCellProps extends React.HTMLAttributes<HTMLDivElement> {
  asChild?: boolean;
}

export const GridCell = ({ asChild, className, ...props }: GridCellProps) => {
  const Comp = asChild ? Slot : "div";
  return <Comp className={cn("truncate", className)} {...props} />;
};

export const GridCellLoading = (props: React.HTMLAttributes<HTMLDivElement>) => (
  <div {...props}>
    <Skeleton className="inline-block h-4 w-3/4" />
  </div>
);

export interface GridLoadingProps extends React.HTMLAttributes<HTMLDivElement> {
  rows?: number;
  columns: number;
}

export const GridLoading: FC<GridLoadingProps> = ({ rows = 1, columns, className, ...props }) => (
  <>
    {Array.from({ length: rows }).map((_, i) => (
      <GridRow className={cn("[&>*:last-child]:text-right", className)} key={i} {...props}>
        {Array.from({ length: columns }).map((_, j) => (
          <GridCellLoading key={j} />
        ))}
      </GridRow>
    ))}
  </>
);

export interface GridEmptyProps {
  icon?: MaterialSymbol;
  title?: ReactNode;
  description?: ReactNode;
  className?: string;
  orientation?: "horizontal" | "vertical";
  action?: ReactNode;
}

export const GridEmpty: FC<PropsWithChildren<GridEmptyProps>> = ({
  icon,
  title,
  description,
  className,
  orientation = "vertical",
  children,
  action,
}) => (
  <div className={cn("col-span-full py-6", className)}>
    <Alert
      className={cn("p-6 text-center", {
        "px-5 py-4 text-left": orientation === "horizontal",
      })}
      variant="default"
    >
      <div
        className={cn("flex flex-col gap-3 flex-1 justify-center", {
          "flex-row items-center": orientation === "horizontal",
        })}
      >
        {icon && (
          <Icon
            icon={icon}
            className={cn("flex-none self-center", {
              "size-5 text-2xl -mt-0.5": orientation === "vertical",
              "size-4 text-lg self-start top-0.5 -ml-1": orientation === "horizontal",
            })}
          />
        )}

        <Group className="gap-0.5 flex-1">
          {title && <AlertTitle className="font-medium">{title}</AlertTitle>}
          {description && <AlertDescription className="text-muted-foreground">{description}</AlertDescription>}
        </Group>

        {(!!children || !!action) && (
          <ButtonGroup className="flex-none justify-center">{children || action}</ButtonGroup>
        )}
      </div>
    </Alert>
  </div>
);

export interface GridRowActionProps extends ButtonProps {
  label?: ReactNode;
}

export const GridRowAction = forwardRef<HTMLButtonElement, GridRowActionProps>(({ children, label, ...props }, ref) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button ref={ref} variant="ghost" size="xs" display="icon" {...props}>
        {children}
      </Button>
    </TooltipTrigger>
    {label && <TooltipContent>{label}</TooltipContent>}
  </Tooltip>
));
GridRowAction.displayName = "GridRowAction";
